/**
 * zones.ts — how loud an authored audio zone is, from where you stand (2026-09-20). PURE.
 *
 * The old client played every zone as ONE point at the zone's centre with a
 * 1/d inverse panner, started and stopped hard on the boundary, and ignored
 * three columns the editor promised: `fadeDuration`, `priority` and `kind`.
 * A 130 m "colony hum" was therefore loud only at its heart (≈ 0.004 at its
 * rim) and clicked on and off as you crossed the line.
 *
 * Two ROLES now, from `kind` (a migration default — a future explicit column
 * can override it):
 *
 *   bed-region  (kind = ambience)  the zone is a ROOM OF EVEN SOUND: full level
 *               through its core, easing to nothing over the outer band. Not
 *               positional — a bed has no "where". Overlapping beds follow
 *               `priority`: the highest plays, those beneath it give way.
 *   point       (kind = sfx | music)  a located emitter at the centre: a real
 *               panner, a distance curve scaled to the zone, audible inside it.
 */

export interface ZoneShape { shapeType: string; cx: number; cy: number; cz: number; radius: number; sx: number; sy: number; sz: number }
export type ZoneRole = 'bed-region' | 'point';
export const roleOfKind = (kind: string): ZoneRole => (kind === 'ambience' ? 'bed-region' : 'point');

/** The outer share of a zone over which a bed eases out (0.25 = the last quarter of the radius). */
export const EDGE_BAND = 0.25;
/** Each priority rank beneath the top plays at this share of the one above it. */
export const PRIORITY_YIELD = 0.35;

const smooth = (a: number, b: number, v: number): number => { const t = Math.min(1, Math.max(0, (v - a) / (b - a))); return t * t * (3 - 2 * t); };

/** 0 at the boundary (and beyond) … 1 at the centre: how deep inside the zone a point is, as a share of its size. */
export function depthInside(z: ZoneShape, x: number, y: number, zz: number): number {
  if (z.shapeType === 'sphere') {
    if (!(z.radius > 0)) return 0;
    return Math.max(0, 1 - Math.hypot(x - z.cx, y - z.cy, zz - z.cz) / z.radius);
  }
  const hx = z.sx / 2, hy = z.sy / 2, hz = z.sz / 2;
  if (!(hx > 0 && hy > 0 && hz > 0)) return 0;
  // The nearest FACE decides (a long hall is "deep" only along its short axis).
  return Math.max(0, Math.min(1 - Math.abs(x - z.cx) / hx, 1 - Math.abs(y - z.cy) / hy, 1 - Math.abs(zz - z.cz) / hz));
}

/** A bed's spatial weight: 0 outside, easing to 1 across the edge band, 1 through the core. */
export function bedEdgeWeight(z: ZoneShape, x: number, y: number, zz: number): number {
  return smooth(0, EDGE_BAND, depthInside(z, x, y, zz));
}

export interface BedInput { id: string; priority: number; gain: number; edge: number }

/**
 * Overlapping beds. The top priority plays at its edge weight; each rank below
 * yields. Equal priorities share a rank. The result is BOUNDED: the summed
 * level never exceeds 1.25 × the loudest single bed — two hums do not add up to
 * a roar just because someone drew them on top of each other.
 */
export function bedGains(beds: readonly BedInput[]): Map<string, number> {
  const out = new Map<string, number>();
  const live = beds.filter((b) => b.edge > 0 && b.gain > 0);
  if (live.length === 0) { for (const b of beds) out.set(b.id, 0); return out; }
  const ranks = [...new Set(live.map((b) => b.priority))].sort((a, b) => b - a);
  let sum = 0, loudest = 0;
  for (const b of live) {
    const g = b.gain * b.edge * Math.pow(PRIORITY_YIELD, ranks.indexOf(b.priority));
    out.set(b.id, g);
    sum += g;
    loudest = Math.max(loudest, b.gain * b.edge);
  }
  const ceiling = loudest * 1.25;
  if (sum > ceiling && sum > 0) { const k = ceiling / sum; for (const [id, g] of out) out.set(id, g * k); }
  for (const b of beds) if (!out.has(b.id)) out.set(b.id, 0);
  return out;
}

/** A point zone's panner: full level out to a quarter of its radius, then 1/d — audible across the zone, not only at its heart. */
export function pointCurve(radius: number): { refDistance: number; rolloff: number } {
  return { refDistance: Math.max(1.5, radius * 0.25), rolloff: 1 };
}
